import { useCallback, useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import {
  Badge,
  Box,
  Button,
  ColorPalette,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  Input,
  Popover,
  PopoverPopup,
  PopoverTrigger,
  ScrollArea,
  Select,
  SelectItem,
  SelectPopup,
  SelectTrigger,
  SelectValue,
  Spinner,
  Switch,
  Text,
  toastManager,
  type Color,
} from "@salestrak/ui";

import { toastMutationError } from "#app/lib/utils/toast-errors";
import { useStageDetail } from "#app/lib/hooks/use-stage-detail";
import { useStageMutations } from "#app/lib/hooks/use-stage-mutations";
import { EntityTag } from "#app/components/entity";
import { SettingsGroup, SettingsGroupContent, SettingsGroupRow } from "#app/components/settings/settings-group";
import { EditableField } from "#app/components/settings/editable-field";
import { DeleteStageDialog } from "#app/components/stages/delete-stage-dialog";
import { PageHeader, PageHeaderBack, PageHeaderTitle } from "#app/components/page-header";
import { withPermission } from "#app/components/permissions/require-permission";


const DEFAULT_AGE = {
  disabled: true,
  allowManualUpdates: false,
  onTrack: { amount: 3, step: "days" },
  atRisk: { amount: 2, step: "weeks" },
};

const STEP_OPTIONS: { label: string; value: string }[] = [
  { label: "Hours", value: "hours" },
  { label: "Days", value: "days" },
  { label: "Weeks", value: "weeks" },
  { label: "Months", value: "months" },
  { label: "Years", value: "years" },
];

function StageEditPage() {
  const { workflowId, stageId } = Route.useParams();
  const { push, replace } = useAppNavigate();
  const { updateStage, setDefaultStage } = useStageMutations();

  const { stage, loading, error } = useStageDetail(stageId);

  // Age settings local state
  const [ageOnTrackAmount, setAgeOnTrackAmount] = useState<string>("");
  const [ageOnTrackStep, setAgeOnTrackStep] = useState("");
  const [ageAtRiskAmount, setAgeAtRiskAmount] = useState<string>("");
  const [ageAtRiskStep, setAgeAtRiskStep] = useState("");
  const [ageDirty, setAgeDirty] = useState(false);
  const [savingAge, setSavingAge] = useState(false);

  // Initialize age fields when navigating to a new stage.
  // Depends only on stageId to avoid resetting user edits on Apollo refetches.
  const [initialized, setInitialized] = useState<string | null>(null);
  useEffect(() => {
    if (!stage || initialized === stageId) return;
    const age = stage.age;
    setAgeOnTrackAmount(String(age?.onTrack?.amount ?? DEFAULT_AGE.onTrack.amount));
    setAgeOnTrackStep(age?.onTrack?.step ?? DEFAULT_AGE.onTrack.step);
    setAgeAtRiskAmount(String(age?.atRisk?.amount ?? DEFAULT_AGE.atRisk.amount));
    setAgeAtRiskStep(age?.atRisk?.step ?? DEFAULT_AGE.atRisk.step);
    setAgeDirty(false);
    setInitialized(stageId);
  }, [stageId, stage, initialized]);

  const [deleteOpen, setDeleteOpen] = useState(false);

  const handleNameSave = useCallback(
    async (_fieldId: string, value: string) => {
      if (!stageId) return;
      await updateStage(stageId, { name: value });
    },
    [stageId, updateStage],
  );

  const stageIconName = stage?.icon?.icon;
  const handleColorChange = useCallback(
    async (color: Color) => {
      if (!stageId) return;
      await updateStage(stageId, {
        icon: { icon: stageIconName, color },
      });
    },
    [stageId, stageIconName, updateStage],
  );

  const handleSetDefault = useCallback(async () => {
    if (!workflowId || !stageId) return;
    try {
      await setDefaultStage(workflowId, stageId);
      toastManager.add({ title: "Stage set as default", type: "success" });
    } catch (err) {
      toastMutationError(err, "Failed to set default stage");
    }
  }, [workflowId, stageId, setDefaultStage]);

  const handleAgeToggle = useCallback(
    async (enabled: boolean) => {
      if (!stageId) return;
      await updateStage(stageId, { age: { disabled: !enabled } });
    },
    [stageId, updateStage],
  );

  const handleManualUpdatesToggle = useCallback(
    async (value: boolean) => {
      if (!stageId) return;
      await updateStage(stageId, { age: { allowManualUpdates: value } });
    },
    [stageId, updateStage],
  );

  const handleSaveAgeDurations = useCallback(async () => {
    if (!stageId) return;
    setSavingAge(true);
    try {
      await updateStage(stageId, {
        age: {
          onTrack: {
            amount: parseInt(ageOnTrackAmount, 10) || DEFAULT_AGE.onTrack.amount,
            step: ageOnTrackStep || DEFAULT_AGE.onTrack.step,
          },
          atRisk: {
            amount: parseInt(ageAtRiskAmount, 10) || DEFAULT_AGE.atRisk.amount,
            step: ageAtRiskStep || DEFAULT_AGE.atRisk.step,
          },
        },
      });
      toastManager.add({
        title: "Age duration settings saved",
        type: "success",
      });
      setAgeDirty(false);
    } catch (err) {
      toastMutationError(err, "Failed to save age settings");
    } finally {
      setSavingAge(false);
    }
  }, [stageId, updateStage, ageOnTrackAmount, ageOnTrackStep, ageAtRiskAmount, ageAtRiskStep]);

  const handleDeleted = useCallback(() => {
    setDeleteOpen(false);
    replace(`/settings/workflows/${workflowId}/stages`);
  }, [replace, workflowId]);

  const handleManageWorkflow = useCallback(() => {
    push(`/settings/workflows/${workflowId}`);
  }, [push, workflowId]);

  if (loading && !stage) {
    return (
      <Box fill="flex" align="center" justify="center">
        <Spinner />
      </Box>
    );
  }

  if (!stage) {
    return (
      <Box fill="flex" align="center" justify="center">
        <Empty>
          <EmptyHeader>
            <EmptyTitle>Unable to load stage</EmptyTitle>
            {error ? (
              <EmptyDescription>{error.message}</EmptyDescription>
            ) : null}
          </EmptyHeader>
        </Empty>
      </Box>
    );
  }

  const ageEnabled = !(stage.age?.disabled ?? DEFAULT_AGE.disabled);

  function durationRow(
    label: string,
    subtitle: string,
    amount: string,
    step: string,
    setAmount: (v: string) => void,
    setStep: (v: string) => void,
  ) {
    return (
      <SettingsGroupRow label={label} subtitle={subtitle}>
        <Box direction="row" gap="sm" align="center">
          <Box width={56}>
            <Input
              value={amount}
              onChange={(event) => {
                setAmount(event.target.value);
                setAgeDirty(true);
              }}
              inputMode="numeric"
            />
          </Box>
          <Box width={130}>
            {/* Base UI's Select is value-based (no `{value,label}` option
                object) and its trigger already fills its container. `items` is
                what makes SelectValue render the LABEL ("Hours") rather than
                the raw value ("hours"); STEP_OPTIONS already has that shape. */}
            <Select
              items={STEP_OPTIONS}
              value={step}
              onValueChange={(next: string | null) => {
                if (next) {
                  setStep(next);
                  setAgeDirty(true);
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Unit" />
              </SelectTrigger>
              <SelectPopup>
                {STEP_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectPopup>
            </Select>
          </Box>
        </Box>
      </SettingsGroupRow>
    );
  }

  return (
    <Box direction="column" fill="flex">
      <ScrollArea>
        <PageHeader centered>
          <PageHeaderBack label="Stages" onClick={() => replace(`/settings/workflows/${workflowId}/stages`)} />
          <PageHeaderTitle
            adornment={
              stage.isDefault ? (
                <Badge variant="secondary" size="sm">
                  <Text>Default</Text>
                </Badge>
              ) : null
            }
          >
            {stage.name}
          </PageHeaderTitle>
        </PageHeader>
        <Box align="center" paddingY="md" paddingX="md">
          <Box gap="xl" fill="width" maxWidth={800}>

            {/* Name & Icon */}
            <SettingsGroup label="General">
              <SettingsGroupRow label="Name & Icon">
                <Box direction="row" gap="sm" align="center">
                  <Popover>
                    <PopoverTrigger
                      render={<Button variant="ghost" size="sm" aria-label="Stage color" />}
                    >
                      <EntityTag
                        entity={{ kind: "stage", id: stage.stageId, stage }}
                        display="visual"
                        size="md"
                        interactive={false}
                      />
                    </PopoverTrigger>
                    <PopoverPopup side="bottom" align="start">
                      <Box padding="md">
                        {/* Box carries no margin props — the label's trailing
                            gap is padding on the label block. */}
                        <Box paddingBottom="sm">
                          <Text size="xs" weight="medium" variant="muted">
                            Color
                          </Text>
                        </Box>
                        <ColorPalette
                          color={stage.icon?.color as Color ?? "teal"}
                          onColorChange={handleColorChange}
                        />
                      </Box>
                    </PopoverPopup>
                  </Popover>
                  <EditableField
                    fieldId="name"
                    label="Stage name"
                    initialValue={stage.name}
                    onSave={handleNameSave}
                    placeholder="Stage name"
                    width="xl"
                  />
                </Box>
              </SettingsGroupRow>
              <SettingsGroupRow
                label="Set stage as default"
                subtitle="Entities added to this workflow will default to this stage."
              >
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSetDefault}
                  disabled={stage.isDefault}
                >
                  <Text>Set as default</Text>
                </Button>
              </SettingsGroupRow>
            </SettingsGroup>

            {/* Age */}
            <Box gap="sm">
              <SettingsGroup
                label="Age"
                subtitle="Displays how long a project has existed in a stage. When enabled, age starts counting when a project enters the stage"
              >
                <SettingsGroupRow label="Enable age">
                  <Switch
                    checked={ageEnabled}
                    onCheckedChange={handleAgeToggle}
                  />
                </SettingsGroupRow>
              </SettingsGroup>

              {ageEnabled ? (
                <SettingsGroup>
                  <SettingsGroupRow
                    label="Manual updates"
                    subtitle="Allow age to be manually updated"
                  >
                    <Switch
                      checked={stage.age?.allowManualUpdates ?? false}
                      onCheckedChange={handleManualUpdatesToggle}
                    />
                  </SettingsGroupRow>
                  {durationRow(
                    "'On Track' duration",
                    "How long until a project is no longer 'On Track'.",
                    ageOnTrackAmount,
                    ageOnTrackStep,
                    setAgeOnTrackAmount,
                    setAgeOnTrackStep,
                  )}
                  {durationRow(
                    "'At Risk' duration",
                    "How long until a project is no longer 'At Risk'.",
                    ageAtRiskAmount,
                    ageAtRiskStep,
                    setAgeAtRiskAmount,
                    setAgeAtRiskStep,
                  )}
                  <SettingsGroupContent>
                    <Box direction="row" justify="end">
                      <Button
                        size="sm"
                        onClick={handleSaveAgeDurations}
                        disabled={!ageDirty || savingAge}
                      >
                        <Text>
                          {savingAge ? "Saving\u2026" : "Apply changes"}
                        </Text>
                      </Button>
                    </Box>
                  </SettingsGroupContent>
                </SettingsGroup>
              ) : null}
            </Box>

            {/* Danger zone */}
            <SettingsGroup label="Danger zone">
              <SettingsGroupRow
                label="Delete stage from workflow"
                subtitle={stage.isDefault ? "Cannot delete default stage. Set another stage as default first." : undefined}
              >
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive-foreground"
                  onClick={() => setDeleteOpen(true)}
                  disabled={stage.isDefault}
                >
                  <Text>Delete stage...</Text>
                </Button>
              </SettingsGroupRow>
            </SettingsGroup>
          </Box>
        </Box>

        <DeleteStageDialog
          stageId={deleteOpen ? stage.stageId : null}
          stageName={stage.name}
          issueCount={stage.issueCount}
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          onDeleted={handleDeleted}
          onManageWorkflow={handleManageWorkflow}
        />
      </ScrollArea>
    </Box>
  );
}

export const Route = createFileRoute(
  "/(app)/$workspace/settings/workflows/$workflowId/stages/$stageId",
)({ component: withPermission("manage.edit_workflows", StageEditPage) });
